import { useState, useEffect, useCallback } from 'react';
import { get } from 'lodash';

// Types
import Portfolio from '@/types/portfolio';
import PortfolioHolding from '@/types/portfolio-holding';
import { PORTFOLIO_METRICS_TABLE_COLUMNS } from '@/enums/Portfolio';
import Fund from '@/types/fund';
import Stock from '@/types/stock';
import Alt from '@/types/models/alt';

// Utils
import { formatMessage, formatLocalizedString } from '@/utils/i18n';
import {
  getHoldingData,
  getMetricValue,
  getLocalizedAndFormattedHoldingValue,
  getLocalizedHoldingValue,
  getTNA,
  Metrics,
  metricsTableI18nBasePath,
} from '../utils/metrics-table';
import { HOLDING_TYPES } from '@/utils/portfolios';

interface UseMetricsDataProps {
  editedPortfolio: Portfolio;
  portfolioHoldings: PortfolioHolding[];
  addPortfolioMetrics: (portfolio: Portfolio) => Promise<Portfolio>;
  getFundData: (id: string) => Promise<Fund>;
  getStockData: (id: string) => Promise<Stock>;
  getAltData: (id: string) => Promise<Alt>;
}

/**
 * Custom hook to fetch and format metrics data for portfolio holdings
 *
 * @param props Object containing all required parameters
 * @returns Object containing metrics data, loading state, and any error
 */
export const useMetricsData = ({ editedPortfolio, portfolioHoldings, addPortfolioMetrics, getFundData, getStockData, getAltData }: UseMetricsDataProps) => {
  const [metricsData, setMetricsData] = useState<Metrics[]>([]);

  const getBaseData = useCallback(async (): Promise<Metrics[]> => {
    const result: Metrics[] = [];

    // Get Portfolio level metrics
    if (portfolioHoldings.length > 0) {
      const portfolioWithMetrics = await addPortfolioMetrics(editedPortfolio);

      result.push({
        CODE: formatMessage(`${metricsTableI18nBasePath}.Total`, { count: portfolioHoldings.length }),
        NAME: '',
        IS_PORTFOLIO: true,
        KEY_METRICS: {
          [PORTFOLIO_METRICS_TABLE_COLUMNS.ONE_YEAR]: getMetricValue(portfolioWithMetrics, 'performance.percentGrowth.1Y'),
          [PORTFOLIO_METRICS_TABLE_COLUMNS.THREE_YEAR]: getMetricValue(portfolioWithMetrics, 'performance.percentGrowth.3Y'),
          [PORTFOLIO_METRICS_TABLE_COLUMNS.BETA]: getMetricValue(portfolioWithMetrics, 'performance.risk.beta.3Y'),
          [PORTFOLIO_METRICS_TABLE_COLUMNS.MER]: getMetricValue(portfolioWithMetrics, 'mer'),
          [PORTFOLIO_METRICS_TABLE_COLUMNS.CLASSIFICATION]: null,
          [PORTFOLIO_METRICS_TABLE_COLUMNS.OBJECTIVE]: null,
          [PORTFOLIO_METRICS_TABLE_COLUMNS.INCEPTION_DATE]: null,
          [PORTFOLIO_METRICS_TABLE_COLUMNS.TNA]: {
            value: null,
            currency: null,
          },
          [PORTFOLIO_METRICS_TABLE_COLUMNS.CURRENCY]: get(portfolioWithMetrics, 'currency', ''),
        },
        ALTERNATIVE_OVERVIEW: {
          [PORTFOLIO_METRICS_TABLE_COLUMNS.FIRM_NAME]: null,
          [PORTFOLIO_METRICS_TABLE_COLUMNS.VINTAGE]: null,
          [PORTFOLIO_METRICS_TABLE_COLUMNS.STATUS]: null,
          [PORTFOLIO_METRICS_TABLE_COLUMNS.COMMITMENT]: getMetricValue(portfolioWithMetrics, 'commitment.size'),
          [PORTFOLIO_METRICS_TABLE_COLUMNS.GP_COMMITMENTS_TO_FUND]: null,
          [PORTFOLIO_METRICS_TABLE_COLUMNS.CALLED]: getMetricValue(portfolioWithMetrics, 'called'),
          [PORTFOLIO_METRICS_TABLE_COLUMNS.DISTRIBUTED]: getMetricValue(portfolioWithMetrics, 'distributed'),
          [PORTFOLIO_METRICS_TABLE_COLUMNS.REMAINING]: getMetricValue(portfolioWithMetrics, 'remaining'),
        },
        FEE_INFORMATION: {
          [PORTFOLIO_METRICS_TABLE_COLUMNS.DURING_INVESTMENT_PERIOD]: getMetricValue(portfolioWithMetrics, 'managementFee.investPeriod'),
          [PORTFOLIO_METRICS_TABLE_COLUMNS.POST_INVESTMENT_PERIOD]: getMetricValue(portfolioWithMetrics, 'managementFee.postInvestPeriod'),
          [PORTFOLIO_METRICS_TABLE_COLUMNS.CHARGE_FREQUENCY]: null,
          [PORTFOLIO_METRICS_TABLE_COLUMNS.HURDLE_RATE]: null,
          [PORTFOLIO_METRICS_TABLE_COLUMNS.CARRIED_INTEREST]: getMetricValue(portfolioWithMetrics, 'carriedInterest.rate'),
          [PORTFOLIO_METRICS_TABLE_COLUMNS.GP_CATCH_UP_RATE]: null,
          [PORTFOLIO_METRICS_TABLE_COLUMNS.FUND_FORMATION_COST]: getMetricValue(portfolioWithMetrics, 'fundFormationCost'),
        },
        ALTERNATIVE_PERFORMANCE: {
          [PORTFOLIO_METRICS_TABLE_COLUMNS.NET_IRR]: getMetricValue(portfolioWithMetrics, 'netIrr'),
          [PORTFOLIO_METRICS_TABLE_COLUMNS.MEDIAN_BENCHMARK_IRR]: getMetricValue(portfolioWithMetrics, 'medianBenchmarkNetIrr'),
          [PORTFOLIO_METRICS_TABLE_COLUMNS.QUARTILE]: null,
        },
      });
    }

    // Get holding level metrics
    for (const portfolioHolding of portfolioHoldings) {
      const holding = await getHoldingData(portfolioHolding, getFundData, getStockData, getAltData);
      const isAltHolding = portfolioHolding.original.type === HOLDING_TYPES.ALT;

      result.push({
        CODE: portfolioHolding.symbol,
        IS_ALT_HOLDING: isAltHolding,
        NAME: formatLocalizedString(portfolioHolding.name),
        KEY_METRICS: {
          [PORTFOLIO_METRICS_TABLE_COLUMNS.ONE_YEAR]: getMetricValue(holding, 'performance.percentGrowth.1Y'),
          [PORTFOLIO_METRICS_TABLE_COLUMNS.THREE_YEAR]: getMetricValue(holding, 'performance.percentGrowth.3Y'),
          [PORTFOLIO_METRICS_TABLE_COLUMNS.BETA]: getMetricValue<number>(holding, 'performance.beta.3Y'),
          [PORTFOLIO_METRICS_TABLE_COLUMNS.MER]: getMetricValue<number>(holding, 'mer'),
          [PORTFOLIO_METRICS_TABLE_COLUMNS.CLASSIFICATION]: getLocalizedAndFormattedHoldingValue(holding, 'classification'),
          [PORTFOLIO_METRICS_TABLE_COLUMNS.OBJECTIVE]: getLocalizedHoldingValue(holding, 'objective'),
          [PORTFOLIO_METRICS_TABLE_COLUMNS.INCEPTION_DATE]: getMetricValue<string>(holding, 'dates.inception'),
          [PORTFOLIO_METRICS_TABLE_COLUMNS.TNA]: getTNA(holding),
          [PORTFOLIO_METRICS_TABLE_COLUMNS.CURRENCY]: getMetricValue<string>(holding, 'currency') || '',
        },
        ALTERNATIVE_OVERVIEW: {
          [PORTFOLIO_METRICS_TABLE_COLUMNS.FIRM_NAME]: getMetricValue<string>(holding, 'manufacturer'),
          [PORTFOLIO_METRICS_TABLE_COLUMNS.VINTAGE]: getMetricValue<number>(holding, 'vintage'),
          [PORTFOLIO_METRICS_TABLE_COLUMNS.STATUS]: getMetricValue<string>(holding, 'status'),
          [PORTFOLIO_METRICS_TABLE_COLUMNS.COMMITMENT]: getMetricValue<number>(holding, 'commitment.size'),
          [PORTFOLIO_METRICS_TABLE_COLUMNS.GP_COMMITMENTS_TO_FUND]: getMetricValue<number>(holding, 'commitment.gpCommitmentValue'),
          [PORTFOLIO_METRICS_TABLE_COLUMNS.CALLED]: getMetricValue<number>(holding, 'called'),
          [PORTFOLIO_METRICS_TABLE_COLUMNS.DISTRIBUTED]: getMetricValue<number>(holding, 'distributed'),
          [PORTFOLIO_METRICS_TABLE_COLUMNS.REMAINING]: getMetricValue<number>(holding, 'remaining'),
        },
        FEE_INFORMATION: {
          [PORTFOLIO_METRICS_TABLE_COLUMNS.DURING_INVESTMENT_PERIOD]: getMetricValue<number>(holding, 'managementFee.investPeriod'),
          [PORTFOLIO_METRICS_TABLE_COLUMNS.POST_INVESTMENT_PERIOD]: getMetricValue<number>(holding, 'managementFee.postInvestPeriod'),
          [PORTFOLIO_METRICS_TABLE_COLUMNS.CHARGE_FREQUENCY]: getMetricValue<string>(holding, 'chargeFrequency'),
          [PORTFOLIO_METRICS_TABLE_COLUMNS.HURDLE_RATE]: getMetricValue<number>(holding, 'hurdleRate'),
          [PORTFOLIO_METRICS_TABLE_COLUMNS.CARRIED_INTEREST]: getMetricValue<number>(holding, 'carriedInterest.rate'),
          [PORTFOLIO_METRICS_TABLE_COLUMNS.GP_CATCH_UP_RATE]: getMetricValue<number>(holding, 'gpCatchUpRate'),
          [PORTFOLIO_METRICS_TABLE_COLUMNS.FUND_FORMATION_COST]: getMetricValue<number>(holding, 'fundFormationCost'),
        },
        ALTERNATIVE_PERFORMANCE: {
          [PORTFOLIO_METRICS_TABLE_COLUMNS.NET_IRR]: getMetricValue<number>(holding, 'netIrr'),
          [PORTFOLIO_METRICS_TABLE_COLUMNS.MEDIAN_BENCHMARK_IRR]: getMetricValue<number>(holding, 'medianBenchmarkNetIrr'),
          [PORTFOLIO_METRICS_TABLE_COLUMNS.QUARTILE]: getMetricValue<string>(holding, 'quartile'),
        },
      });
    }

    return result;
  }, [portfolioHoldings, editedPortfolio, addPortfolioMetrics, getFundData, getStockData, getAltData]);

  useEffect(() => {
    async function fetchData() {
      try {
        const data = await getBaseData();
        // Make sure CODE and NAME are never undefined
        // because Tanstack sorting doesn't handle undefined values well
        const formattedData = data.map(d => ({
          ...d,
          CODE: d.CODE ? d.CODE : '',
          NAME: d.NAME ? d.NAME : '',
        }));

        setMetricsData(formattedData);
      } catch (err) {
        setMetricsData([]);
      }
    }

    fetchData();
  }, [portfolioHoldings, editedPortfolio, addPortfolioMetrics, getFundData, getStockData, getAltData, getBaseData]);

  return { metricsData };
};
